import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ReportApprovalStatus } from '@core/enums/report-approval-status';
import { Project } from '@core/interfaces/items/project';
import { TaskToDownload } from '@core/interfaces/offline/task-to-download';
import { TaskOfflineRequest } from '@core/interfaces/requests/task-offline-request';
import { TasksOfflineData } from '@core/interfaces/tasks-offline-data';
import { VisualEvent } from '@core/interfaces/visual-event';
import { DatabaseService } from '@core/services/database.service';
import { DeviceService } from '@core/services/device.service';
import { UrlService } from '@core/services/url.service';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from 'osc-reference-app-common';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import moment from 'moment';
import { LoggerService } from '@shared/services/logger.service';
import { NativeFileService } from '@shared/services/native-file.service';
import { ReportDataFetcherService } from '@shared/services/report-data-fetcher.service';
import { Tasks } from '@orm-entities/tasks';
import { TasksStatusService } from 'src/app/mobile/services/tasks-status.service';
import { DEVICE_ID_HEADER } from '@shared/headers';
import { UserProfile } from '@osc-utils/models/user-profile';
import { UserSignature } from '@osc-utils/models/user-signature';
import { ApprovalsService } from '@shared/services/approvals.service';
import { VisualEventsFetcherService } from '@shared/services/visual-events-fetcher.service';
import { TreeItemStatus } from '../enums/tree-item-status';
import { CMLConfigurationService } from '../../mobile/services/cml-configuration.service';
import { DownloadTaskProgressService } from './download-task-progress.service';
import { CurrentUserService } from './current-user.service';

@Injectable({
  providedIn: 'root',
})
export class OfflineDataService {
  #httpClient = inject(HttpClient);
  #urlService = inject(UrlService);
  #deviceService = inject(DeviceService);
  #downloadTaskProgressService = inject(DownloadTaskProgressService);
  #translate = inject(TranslateService);
  #approvalsService = inject(ApprovalsService);
  #databaseService = inject(DatabaseService);
  #visualEventsFetcherService = inject(VisualEventsFetcherService);
  #logger = inject(LoggerService);
  #nativeFileService = inject(NativeFileService);
  #snackBarService = inject(SnackBarService);
  #tasksStatusService = inject(TasksStatusService);
  #reportDataFetcherService = inject(ReportDataFetcherService);
  #currentUserService = inject(CurrentUserService);
  protected readonly cmlConfigurationService = inject(CMLConfigurationService);

  public async triggerOfflineDbMail(): Promise<boolean> {
    try {
      const zipFileName = 'offline-backup.zip';
      const blobData = await this.#nativeFileService.readFileAsBlob({ path: zipFileName });
      const formData = new FormData();
      formData.append('file', blobData);
      formData.append('fileName', zipFileName);
      const url = this.#urlService.getUrl(`inspection-requests/send-inspection-offline-db`);
      await firstValueFrom(this.#httpClient.post<boolean>(url, formData));
      return true;
    } catch {
      return false;
    }
  }

  public async downloadTask(task: TaskToDownload, databaseService: DatabaseService, projects: Project[]): Promise<void> {
    const downloadedTaskIds = await this.getDownloadedTaskIds();
    if (downloadedTaskIds.includes(task.id)) {
      return;
    }

    this.#downloadTaskProgressService.setProgressValueForTasks([task.id], 40);

    this.#tasksStatusService.setTaskAreDownloading([task.id]);

    const rejectedTaskComment = await firstValueFrom(this.getRejectedTaskComment(task.id, task.projectId, task.jobId));
    const taskApprovalStatus = {
      status: rejectedTaskComment ? ReportApprovalStatus.Rejected : ReportApprovalStatus.Pending,
      comment: rejectedTaskComment || '',
    };
    await firstValueFrom(this.cmlConfigurationService.fetchAndPersistLocally());
    const currentUserProfile = this.#currentUserService.currentUser() as UserProfile;

    let failureAlreadyHandled = false;
    try {
      const [data, allVisualEvents, inspectionReportData, userSignature] = await Promise.all([
        this.downloadTasksData(task),
        this.downloadAllVisualEvent(task),
        firstValueFrom(this.#reportDataFetcherService.fetchData(task.projectId, task.jobId, task.id)),
        this.downloadUserSignature(),
      ]);

      this.#downloadTaskProgressService.setProgressValueForTasks([task.id], 60);

      const inspectionReportDataItems = [
        {
          taskId: task.id,
          inspectionReportData: JSON.stringify(inspectionReportData),
        },
      ];
      const isApplicationAdmin = currentUserProfile.isAdmin();
      this.#downloadTaskProgressService.setProgressValueForTasks([task.id], 80);

      const filteredProjects = projects.filter(project => project.id === task.projectId);
      const signatureData = userSignature?.signatureBase64 ? userSignature : { signatureBase64: '', userId: currentUserProfile.id };

      const isDataStored = await databaseService.storeData(
        currentUserProfile,
        isApplicationAdmin,
        filteredProjects,
        data,
        inspectionReportDataItems,
        signatureData,
        taskApprovalStatus,
        task,
        allVisualEvents,
        TreeItemStatus.InProgress,
      );

      if (isDataStored) {
        this.#downloadTaskProgressService.setProgressValueForTasks([task.id], 100);
        this.#tasksStatusService.setTaskAreDownloaded();
        localStorage.setItem('lastTaskDownload', moment().format());
        await this.setTaskAsInProgress(task);
      } else {
        failureAlreadyHandled = true;
        this.setDownloadFailedStatusForTask(task, '');
        throw new Error('Failed to store task data');
      }
    } catch (error) {
      if (!failureAlreadyHandled) {
        this.setDownloadFailedStatusForTask(task, error);
      }
      throw error;
    }
  }

  private getRejectedTaskComment(taskId: string, projectId: string, jobId: string): Observable<string | null> {
    return this.#approvalsService.fetchProjectApprovals(projectId).pipe(
      map(approvals => {
        const taskApprovals = approvals.filter((res: any) => res.taskId === taskId);
        for (const approval of taskApprovals) {
          if (approval.approver?.status === ReportApprovalStatus.Rejected) {
            return approval.approver.comment || null;
          }
        }
        return null;
      }),
    );
  }

  private async downloadTasksData(task: TaskToDownload): Promise<TasksOfflineData> {
    const request: TaskOfflineRequest = {
      tasks: [
        {
          taskId: task.id,
          hubId: task.projectId,
          jobId: task.jobId,
        },
      ],
    };

    const url = this.#urlService.getUrl('tasks/data');
    return firstValueFrom(
      this.#httpClient.post<TasksOfflineData>(url, request, {
        headers: new HttpHeaders({
          [DEVICE_ID_HEADER]: this.#deviceService.getDeviceId(),
        }),
      }),
    );
  }

  private async downloadAllVisualEvent(task: TaskToDownload): Promise<VisualEvent[][]> {
    return Promise.all([firstValueFrom(this.#visualEventsFetcherService.fetchVisualEvents(task.id))]);
  }

  private async downloadUserSignature(): Promise<UserSignature> {
    const url = this.#urlService.getUrl(`users/current/signature`);
    return firstValueFrom(this.#httpClient.get<UserSignature>(url).pipe(catchError(() => of({ signatureBase64: '' }))));
  }

  private setDownloadFailedStatusForTask(task: TaskToDownload, error: any) {
    this.#downloadTaskProgressService.downloadFailedForTasks([task.id]);
    this.#tasksStatusService.setTasksDownloadHaveFailed([task.id]);
    this.#snackBarService.error(this.#translate.instant('offlineData.downloadTasksError'));
    this.#logger.error('Error downloading tasks', error);
  }

  private async getDownloadedTaskIds(): Promise<string[]> {
    const ids: string[] = [];
    const tasks = await firstValueFrom(this.#databaseService.selectDistinct(Tasks));
    tasks.forEach(task => {
      ids.push(task.id);
    });
    return ids;
  }

  private async setTaskAsInProgress(task: TaskToDownload): Promise<void> {
    const url = this.#urlService.getUrl(`hubs/${task.projectId}/jobs/${task.jobId}/tasks/${task.id}/status`);
    await firstValueFrom(this.#httpClient.put(url, { status: TreeItemStatus.InProgress }));
  }
}
